import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes, FaCalendarAlt, FaTags,
  FaChevronDown, FaCheckCircle, FaRupeeSign, FaChartBar,
  FaHandHoldingHeart, FaPray, FaMoneyBillWave, FaBalanceScale,
  FaPrint, FaFilePdf, FaArrowUp, FaArrowDown, FaGlobe, FaDownload,
  FaHourglassHalf, FaTimesCircle, FaEye
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ══════════════════════════════════════════════════════════
   TOAST
══════════════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const cfg = {
    success: { color: "#2a6b2a", bg: "rgba(42,107,42,0.12)", border: "rgba(42,107,42,0.3)", icon: <FaCheckCircle />, sym: "✦" },
    error:   { color: "#8b1a1a", bg: "rgba(139,26,26,0.10)", border: "rgba(139,26,26,0.28)", icon: <FaTimes />, sym: "✗" },
    info:    { color: "#8a6020", bg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.3)", icon: <FaSearch />, sym: "ॐ" },
  };
  const c = cfg[type] || cfg.info;
  return (
    <div className="mb-toast-wrap">
      <div className="mb-toast" style={{ "--ta": c.color, "--tb": c.border }}>
        <div className="mb-toast-bar" />
        <span className="mb-mc tl"/><span className="mb-mc tr"/><span className="mb-mc bl"/><span className="mb-mc br"/>
        <div className="mb-toast-icon" style={{ background: c.bg, borderColor: c.border, color: c.color }}>{c.icon}</div>
        <div className="mb-toast-body">
          <div className="mb-toast-title" style={{ color: c.color }}>{c.sym} {type.toUpperCase()} {c.sym}</div>
          <p className="mb-toast-msg">{message}</p>
        </div>
        <button className="mb-toast-x" onClick={onClose} style={{ color: c.color }}>✕</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PDF PREVIEW MODAL  (carried over from Areports, mobile numbers stripped)
══════════════════════════════════════════════════════════ */
function PdfPreviewModal({ reportData, filters, onClose, t, i18n }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);

  const fmtAmt  = (n) => `₹${parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2})}`;
  const fmtDate = (v) => { if (!v) return "—"; const d = new Date(v); return isNaN(d)?v:d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); };

  const periodLabel = (() => {
    if (filters.specificDate) return new Date(filters.specificDate).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"});
    if (filters.month) { const [y,m]=filters.month.split("-"); return new Date(y,m-1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); }
    if (filters.year) return `Year ${filters.year}`;
    if (filters.dateRange?.start||filters.dateRange?.end) {
      const s=filters.dateRange.start?new Date(filters.dateRange.start).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}):"Beginning";
      const e=filters.dateRange.end?new Date(filters.dateRange.end).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}):"Present";
      return `${s} — ${e}`;
    }
    return t("rptFilterAllTime")||"All Records";
  })();

  const { donations, bookings, expenses } = reportData;
  const totalDonations = donations.reduce((s,d)=>s+parseFloat(d.amount||0),0);
  const totalBookings  = bookings.reduce((s,b)=>s+parseFloat(b.amount||0),0);
  const totalIncome    = totalDonations+totalBookings;
  const totalExpenses  = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const netBalance     = totalIncome-totalExpenses;

  const donByType={};
  donations.forEach(d=>{ const k=d.donation_type||"Other"; if(!donByType[k])donByType[k]={count:0,total:0}; donByType[k].count++;donByType[k].total+=parseFloat(d.amount||0); });
  const bkByType={};
  bookings.forEach(b=>{ const k=b.category_name||"Other"; if(!bkByType[k])bkByType[k]={count:0,total:0}; bkByType[k].count++;bkByType[k].total+=parseFloat(b.amount||0); });
  const expByCat={};
  expenses.forEach(e=>{ const cat=e.category_title||"Uncategorized"; if(!expByCat[cat])expByCat[cat]={items:[],type:e.category_type||""}; expByCat[cat].items.push(e); });

  const handlePrint = () => {
    const pw = window.open("","_blank");
    const css = `
      @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Noto+Sans+Malayalam:wght@400;700&display=swap');
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:'EB Garamond',serif;background:#fff;color:#1a0404;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .pdf-wrap{max-width:900px;margin:0 auto;}
      .pdf-header{background:linear-gradient(135deg,#2e0c0c 0%,#5a1515 60%,#3d0c0c 100%);padding:36px 52px 28px;position:relative;overflow:hidden;}
      .pdf-header::before{content:'ॐ';position:absolute;right:36px;top:50%;transform:translateY(-50%);font-size:130px;color:rgba(232,196,106,0.06);font-family:'Cinzel',serif;line-height:1;pointer-events:none;}
      .pdf-header-gold-bar{position:absolute;bottom:0;left:0;right:0;height:4px;background:linear-gradient(90deg,transparent 0%,#8a6020 20%,#e8c46a 50%,#8a6020 80%,transparent 100%);}
      .pdf-hdr-row1{display:flex;align-items:center;gap:20px;margin-bottom:18px;}
      .pdf-hdr-seal{width:72px;height:72px;border-radius:50%;background:rgba(232,196,106,0.1);border:2px solid rgba(232,196,106,0.4);display:flex;align-items:center;justify-content:center;font-size:28px;color:#e8c46a;font-family:'Cinzel',serif;flex-shrink:0;}
      .pdf-hdr-temple-name{font-family:'Cinzel',serif;font-size:14px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;}
      .pdf-hdr-temple-sub{font-family:'EB Garamond',serif;font-size:13px;color:rgba(232,196,106,0.5);letter-spacing:1px;margin-top:5px;}
      .pdf-hdr-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(232,196,106,0.35),transparent);margin:0 0 16px;}
      .pdf-hdr-row2{display:flex;align-items:flex-end;justify-content:space-between;}
      .pdf-hdr-report-label{font-family:'Cinzel',serif;font-size:11px;font-weight:600;letter-spacing:3px;text-transform:uppercase;color:rgba(232,196,106,0.6);margin-bottom:5px;}
      .pdf-hdr-report-title{font-family:'Cinzel',serif;font-size:20px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;}
      .pdf-hdr-period{font-family:'EB Garamond',serif;font-size:14px;color:rgba(232,196,106,0.8);margin-top:5px;font-style:italic;}
      .pdf-hdr-meta{text-align:right;}
      .pdf-hdr-meta-line{font-family:'Cinzel',serif;font-size:10px;color:rgba(232,196,106,0.45);letter-spacing:1px;line-height:1.8;}
      .pdf-body{padding:32px 52px 48px;}
      .pdf-summary-strip{display:grid;grid-template-columns:repeat(5,1fr);border:1.5px solid #e0c070;border-radius:6px;overflow:hidden;margin-bottom:36px;}
      .pdf-sc{padding:16px 12px;text-align:center;border-right:1px solid #e8d89a;}
      .pdf-sc:last-child{border-right:none;}
      .pdf-sc-label{font-family:'Cinzel',serif;font-size:7px;letter-spacing:1.5px;text-transform:uppercase;color:#8a6020;margin-bottom:8px;}
      .pdf-sc-val{font-family:'Cinzel',serif;font-size:15px;font-weight:700;}
      .pdf-sc-val.green{color:#1a5c1a;} .pdf-sc-val.red{color:#8b1a1a;}
      .pdf-sc.income{background:linear-gradient(180deg,#f0fff0,#fff);} .pdf-sc.expense{background:linear-gradient(180deg,#fff5f5,#fff);}
      .pdf-sc.balance-pos{background:linear-gradient(180deg,#e8f5e8,#f0fff0);} .pdf-sc.balance-neg{background:linear-gradient(180deg,#fce8e8,#fff5f5);}
      .pdf-section{margin-bottom:34px;page-break-inside:avoid;}
      .pdf-section-header{display:flex;align-items:stretch;gap:0;margin-bottom:14px;}
      .pdf-sec-accent{width:5px;background:linear-gradient(180deg,#8b1a1a,#c9973a);border-radius:3px;flex-shrink:0;margin-right:12px;}
      .pdf-sec-accent.green{background:linear-gradient(180deg,#2a6b2a,#5a9a5a);} .pdf-sec-accent.gold{background:linear-gradient(180deg,#8a6020,#c9973a);}
      .pdf-sec-title-block{flex:1;border-bottom:1.5px solid #e0c070;padding-bottom:8px;}
      .pdf-sec-title{font-family:'Cinzel',serif;font-size:12px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#3d0c0c;}
      .pdf-sec-subtitle{font-family:'EB Garamond',serif;font-size:12px;color:#b8a070;margin-top:3px;}
      table{width:100%;border-collapse:collapse;font-size:12px;font-family:'EB Garamond',serif;}
      thead tr{background:linear-gradient(135deg,#2e0c0c,#4a1010);}
      thead th{font-family:'Cinzel',serif;font-size:7.5px;letter-spacing:1.5px;text-transform:uppercase;color:#e8c46a;padding:10px 11px;text-align:left;font-weight:600;border-bottom:2px solid #c9973a;}
      tbody tr{border-bottom:1px solid #f0e8d0;} tbody tr:nth-child(even){background:#fffbf3;}
      tbody td{padding:9px 11px;color:#2c1008;vertical-align:top;line-height:1.45;}
      .td-amount{font-family:'Cinzel',serif;font-weight:700;text-align:right;color:#1a5c1a;white-space:nowrap;}
      .td-amount.red{color:#8b1a1a;} .td-num{text-align:center;color:#c9973a;font-family:'Cinzel',serif;font-size:10px;font-weight:600;}
      .td-pill{display:inline-block;font-family:'Cinzel',serif;font-size:7.5px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;padding:3px 8px;border:1px solid rgba(201,151,58,0.35);border-radius:20px;color:#7a5020;background:rgba(201,151,58,0.1);}
      tfoot tr{border-top:2px solid #c9973a;}
      tfoot td{background:linear-gradient(135deg,#3d0c0c,#2e0c0c);font-family:'Cinzel',serif;font-size:11px;font-weight:700;padding:10px 11px;color:#e8c46a;letter-spacing:1px;}
      .tfoot-amt{color:#f5e090;text-align:right;}
      .pdf-fin-summary{border:1.5px solid #e0c070;border-radius:6px;overflow:hidden;margin-top:4px;}
      .pdf-fs-row{display:flex;align-items:center;justify-content:space-between;padding:12px 20px;border-bottom:1px solid #f0e8d0;}
      .pdf-fs-row:last-child{border-bottom:none;}
      .pdf-fs-label{font-family:'EB Garamond',serif;font-size:14px;color:#4a1a08;display:flex;align-items:center;gap:8px;}
      .pdf-fs-label .dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
      .pdf-fs-val{font-family:'Cinzel',serif;font-size:14px;font-weight:700;}
      .pdf-fs-val.green{color:#1a5c1a;} .pdf-fs-val.red{color:#8b1a1a;}
      .pdf-fs-row.gross{background:rgba(42,107,42,0.06);}
      .pdf-fs-row.gross .pdf-fs-label{font-family:'Cinzel',serif;font-size:12px;font-weight:700;color:#1a4a1a;letter-spacing:1px;}
      .pdf-fs-row.gross .pdf-fs-val{font-size:16px;}
      .pdf-fs-row.net-pos{background:linear-gradient(90deg,rgba(42,107,42,0.1),rgba(42,107,42,0.04));border-top:2px solid #2a6b2a;}
      .pdf-fs-row.net-neg{background:linear-gradient(90deg,rgba(139,26,26,0.1),rgba(139,26,26,0.04));border-top:2px solid #8b1a1a;}
      .pdf-fs-row.net-pos .pdf-fs-label,.pdf-fs-row.net-neg .pdf-fs-label{font-family:'Cinzel',serif;font-size:13px;font-weight:700;letter-spacing:1px;}
      .pdf-fs-row.net-pos .pdf-fs-val,.pdf-fs-row.net-neg .pdf-fs-val{font-size:20px;}
      .pdf-footer{margin-top:40px;padding:18px 0 0;border-top:1px solid #e0c070;display:flex;align-items:center;justify-content:space-between;gap:20px;}
      .pdf-footer-text{font-family:'EB Garamond',serif;font-size:11px;color:#c0a878;line-height:1.8;font-style:italic;}
      .pdf-footer-om{font-size:28px;color:rgba(139,26,26,0.18);}
      .pdf-footer-seal-text{font-family:'Cinzel',serif;font-size:7px;letter-spacing:2px;color:#d4b87a;text-transform:uppercase;text-align:center;margin-top:3px;}
      @media print{@page{margin:12mm 14mm;size:A4;} body{-webkit-print-color-adjust:exact;} .pdf-section{page-break-inside:avoid;} thead{display:table-header-group;}}
    `;
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Temple Financial Report</title><style>${css}</style></head><body><div class="pdf-wrap">${printRef.current?.innerHTML||""}</div></body></html>`);
    pw.document.close(); pw.focus();
    setTimeout(()=>{pw.print();pw.close();},900);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const loadScript = (src) => new Promise((res,rej)=>{ if(document.querySelector(`script[src="${src}"]`)){res();return;} const s=document.createElement("script");s.src=src;s.onload=res;s.onerror=rej;document.head.appendChild(s); });
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      const container=document.createElement("div");
      container.style.cssText="position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-1;";
      const styleEl=document.createElement("style"); styleEl.textContent=handlePrint.toString().match(/const css = `([\s\S]*?)`/)?.[1]||"";
      const contentDiv=document.createElement("div"); contentDiv.className="pdf-wrap"; contentDiv.innerHTML=printRef.current?.innerHTML||"";
      container.appendChild(styleEl); container.appendChild(contentDiv); document.body.appendChild(container);
      await new Promise(r=>setTimeout(r,600));
      const canvas=await window.html2canvas(contentDiv,{scale:2,useCORS:true,backgroundColor:"#ffffff",logging:false});
      document.body.removeChild(container);
      const {jsPDF}=window.jspdf;
      const pdf=new jsPDF({unit:"mm",format:"a4",orientation:"portrait"});
      const pageW=pdf.internal.pageSize.getWidth(),pageH=pdf.internal.pageSize.getHeight(),margin=10;
      const imgW=pageW-margin*2,imgH=(canvas.height*imgW)/canvas.width;
      const imgData=canvas.toDataURL("image/jpeg",0.95);
      let yPos=margin,remainingH=imgH;
      while(remainingH>0){ const sliceH=Math.min(remainingH,pageH-margin*2),srcY=(imgH-remainingH)*(canvas.height/imgH),srcHh=sliceH*(canvas.height/imgH);
        const pc=document.createElement("canvas"); pc.width=canvas.width; pc.height=srcHh;
        pc.getContext("2d").drawImage(canvas,0,srcY,canvas.width,srcHh,0,0,canvas.width,srcHh);
        pdf.addImage(pc.toDataURL("image/jpeg",0.95),"JPEG",margin,yPos,imgW,sliceH);
        remainingH-=sliceH; if(remainingH>0){pdf.addPage();yPos=margin;} }
      pdf.save(`Temple-Financial-Report-${periodLabel.replace(/[^a-z0-9]/gi,"-")}.pdf`);
    } catch(err){ alert("PDF download failed: "+(err?.message||String(err))); }
    finally{ setDownloading(false); }
  };

  useEffect(()=>{ const h=(e)=>{if(e.key==="Escape")onClose();}; document.addEventListener("keydown",h); return()=>document.removeEventListener("keydown",h); },[onClose]);

  return createPortal(
    <div className="rpt-overlay" onClick={onClose}>
      <div className="rpt-pdf-modal" onClick={e=>e.stopPropagation()}>

        {/* Modal header */}
        <div className="rpt-pdf-modal-header">
          <div className="rpt-pdf-modal-title">
            <div className="rpt-pdf-hicon"><FaFilePdf /></div>
            <div>
              <div className="rpt-pdf-eyebrow">✦ {t("rptPdfPreviewTitle")||"Report Preview"} ✦</div>
              <div className="rpt-pdf-modal-name">{t("rptPdfFinancialReport")||"Financial Report"} — {periodLabel}</div>
            </div>
          </div>
          <div className="rpt-pdf-modal-actions">
            <button className="rpt-pdf-action-btn rpt-pdf-print" onClick={handlePrint}><FaPrint /> {t("rptPdfPrintBtn")||"Print"}</button>
            <button className="rpt-pdf-action-btn rpt-pdf-download" onClick={handleDownload} disabled={downloading}>
              {downloading?<><span className="rpt-pdf-spin">◌</span> Downloading…</>:<><FaDownload /> {t("rptBtnDownloadPdf")||"Download PDF"}</>}
            </button>
            <button className="rpt-pdf-action-btn rpt-pdf-close-x" onClick={onClose}><FaTimes /></button>
          </div>
        </div>

        {/* Scrollable paper */}
        <div className="rpt-pdf-scroll">
          <div className="rpt-pdf-canvas">
            <div className="rpt-pdf-paper" ref={printRef}>

              {/* ── PDF HEADER ── */}
              <div className="pdf-header">
                <div className="pdf-header-gold-bar" />
                <div className="pdf-hdr-row1">
                  <div className="pdf-hdr-seal"><span>ॐ</span></div>
                  <div>
                    <div className="pdf-hdr-temple-name">Kodungallur Kavil · Sree Bhadrakali Devi Temple</div>
                    <div className="pdf-hdr-temple-sub">Pandanad · Chengannur · Kerala</div>
                  </div>
                </div>
                <div className="pdf-hdr-divider" />
                <div className="pdf-hdr-row2">
                  <div>
                    <div className="pdf-hdr-report-label">{t("rptPdfStatementOfAccounts")||"Statement of Accounts"}</div>
                    <div className="pdf-hdr-report-title">{t("rptPdfFinancialReport")||"Financial Report"}</div>
                    <div className="pdf-hdr-period">{periodLabel}</div>
                  </div>
                  <div className="pdf-hdr-meta">
                    <div className="pdf-hdr-meta-line">{t("rptPdfGeneratedOn")||"Generated on"}: {new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</div>
                    <div className="pdf-hdr-meta-line">{t("rptPdfRecords")||"Records"}: {donations.length + bookings.length + expenses.length}</div>
                    <div className="pdf-hdr-meta-line">{t("rptPdfTempleRecords")||"Temple Records"}</div>
                  </div>
                </div>
              </div>

              <div className="pdf-body">
                {/* Summary strip */}
                <div className="pdf-summary-strip">
                  <div className="pdf-sc income"><div className="pdf-sc-label">{t("rptStatDonations")||"Donations"}</div><div className="pdf-sc-val green">{fmtAmt(totalDonations)}</div><div className="pdf-sc-note">{donations.length} records</div></div>
                  <div className="pdf-sc income"><div className="pdf-sc-label">{t("rptStatOfferings")||"Offerings"}</div><div className="pdf-sc-val green">{fmtAmt(totalBookings)}</div><div className="pdf-sc-note">{bookings.length} records</div></div>
                  <div className="pdf-sc income"><div className="pdf-sc-label">{t("rptPdfGrossIncome")||"Gross Income"}</div><div className="pdf-sc-val green">{fmtAmt(totalIncome)}</div></div>
                  <div className="pdf-sc expense"><div className="pdf-sc-label">{t("rptTotalExpenses")||"Expenses"}</div><div className="pdf-sc-val red">{fmtAmt(totalExpenses)}</div><div className="pdf-sc-note">{expenses.length} entries</div></div>
                  <div className={`pdf-sc ${netBalance>=0?"balance-pos":"balance-neg"}`}><div className="pdf-sc-label">{t("rptStatNetBalance")||"Net Balance"}</div><div className={`pdf-sc-val ${netBalance>=0?"green":"red"}`}>{fmtAmt(netBalance)}</div></div>
                </div>

                {/* Section 1: Donations */}
                <div className="pdf-section">
                  <div className="pdf-section-header">
                    <div className="pdf-sec-accent green" />
                    <div className="pdf-sec-title-block">
                      <div className="pdf-sec-title">{t("rptPdfSection1")||"1 · Donations & Sambhavana"}</div>
                      <div className="pdf-sec-subtitle">{donations.length} records · Total: {fmtAmt(totalDonations)}</div>
                    </div>
                  </div>
                  {donations.length > 0 && (
                    <table>
                      <thead><tr><th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColReceipt")||"Receipt"}</th><th>{t("rptColDonorName")||"Donor"}</th><th>{t("rptColType")||"Type"}</th><th>{t("rptColSource")||"Source"}</th><th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th></tr></thead>
                      <tbody>
                        {donations.map((d,i)=>(
                          <tr key={d.donation_id||i}>
                            <td className="td-num">{i+1}</td>
                            <td>{fmtDate(d.created_at)}</td>
                            <td style={{fontFamily:"'Cinzel',serif",fontSize:11,color:"#7a5020"}}>{d.receipt_number||"—"}</td>
                            <td>{d.name||"—"}</td>
                            <td><span className="td-pill">{d.donation_type||"—"}</span></td>
                            <td><span className="td-pill" style={{borderColor:d.booked_by==="online"?"rgba(42,107,42,0.3)":"rgba(138,96,32,0.3)",color:d.booked_by==="online"?"#2a6b2a":"#8a6020",background:d.booked_by==="online"?"rgba(42,107,42,0.07)":"rgba(138,96,32,0.07)"}}>{d.booked_by==="online"?"Online":"Counter"}</span></td>
                            <td className="td-amount">{fmtAmt(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan="6" style={{textAlign:"right",padding:"10px 14px"}}>{t("rptTotalDonations")||"Total Donations"}</td><td className="tfoot-amt" style={{padding:"10px 14px",textAlign:"right"}}>{fmtAmt(totalDonations)}</td></tr></tfoot>
                    </table>
                  )}
                  {/* Donation type breakdown */}
                  {Object.keys(donByType).length > 1 && (
                    <div style={{marginTop:10,marginLeft:"auto",width:"44%",border:"1px solid #e8d89a",borderRadius:4,overflow:"hidden"}}>
                      <table><thead><tr style={{background:"linear-gradient(135deg,#f5e8cc,#ede0b0)"}}><th style={{color:"#5a3010",fontSize:7}}>Type</th><th style={{color:"#5a3010",fontSize:7,textAlign:"center"}}>Count</th><th style={{color:"#5a3010",fontSize:7,textAlign:"right"}}>Total</th></tr></thead>
                        <tbody>{Object.entries(donByType).map(([k,v])=><tr key={k}><td style={{background:"#fff"}}>{k}</td><td style={{background:"#fff",textAlign:"center"}}>{v.count}</td><td className="td-amount" style={{background:"#fff"}}>{fmtAmt(v.total)}</td></tr>)}</tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section 2: Offerings */}
                <div className="pdf-section">
                  <div className="pdf-section-header">
                    <div className="pdf-sec-accent gold" />
                    <div className="pdf-sec-title-block">
                      <div className="pdf-sec-title">{t("rptPdfSection2")||"2 · Vazhipadu / Offerings"}</div>
                      <div className="pdf-sec-subtitle">{bookings.length} bookings · Total: {fmtAmt(totalBookings)}</div>
                    </div>
                  </div>
                  {bookings.length > 0 && (
                    <table>
                      <thead><tr><th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColReceipt")||"Receipt"}</th><th>{t("rptColVazhipadu")||"Vazhipadu"}</th><th style={{textAlign:"center"}}>{t("rptColPersons")||"Persons"}</th><th>{t("rptColSource")||"Source"}</th><th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th></tr></thead>
                      <tbody>
                        {bookings.map((b,i)=>(
                          <tr key={b.booking_id||i}>
                            <td className="td-num">{i+1}</td>
                            <td>{fmtDate(b.booking_date||b.created_at)}</td>
                            <td style={{fontFamily:"'Cinzel',serif",fontSize:11,color:"#7a5020"}}>{b.receipt_number||"—"}</td>
                            <td><span className="td-pill">{b.category_name||"—"}</span></td>
                            <td style={{textAlign:"center"}}>{b.total_persons||1}</td>
                            <td><span className="td-pill" style={{borderColor:b.booked_by==="online"?"rgba(42,107,42,0.3)":"rgba(138,96,32,0.3)",color:b.booked_by==="online"?"#2a6b2a":"#8a6020",background:b.booked_by==="online"?"rgba(42,107,42,0.07)":"rgba(138,96,32,0.07)"}}>{b.booked_by==="online"?"Online":"Counter"}</span></td>
                            <td className="td-amount">{fmtAmt(b.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan="6" style={{textAlign:"right",padding:"10px 14px"}}>{t("rptTotalOfferings")||"Total Offerings"}</td><td className="tfoot-amt" style={{padding:"10px 14px",textAlign:"right"}}>{fmtAmt(totalBookings)}</td></tr></tfoot>
                    </table>
                  )}
                </div>

                {/* Section 3: Expenses */}
                <div className="pdf-section">
                  <div className="pdf-section-header">
                    <div className="pdf-sec-accent" />
                    <div className="pdf-sec-title-block">
                      <div className="pdf-sec-title">{t("rptPdfSection3")||"3 · Expenses"}</div>
                      <div className="pdf-sec-subtitle">{expenses.length} entries · Total: {fmtAmt(totalExpenses)}</div>
                    </div>
                  </div>
                  {Object.entries(expByCat).map(([cat, { items, type }]) => {
                    const catTotal = items.reduce((s,e)=>s+parseFloat(e.amount||0),0);
                    return (
                      <div key={cat}>
                        <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0 6px",padding:"7px 12px",background:"linear-gradient(90deg,rgba(201,151,58,0.1),transparent)",borderLeft:"3px solid #c9973a",borderRadius:"0 4px 4px 0"}}>
                          <span style={{fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,color:"#5a3010",letterSpacing:1,textTransform:"uppercase"}}>{cat}</span>
                          {type && <span style={{fontSize:11,color:"#b8a070",fontStyle:"italic"}}>({type})</span>}
                          <span style={{marginLeft:"auto",fontFamily:"'Cinzel',serif",fontSize:11,fontWeight:700,color:"#8b1a1a"}}>{fmtAmt(catTotal)}</span>
                        </div>
                        <table>
                          <thead><tr><th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColTitle")||"Title"}</th><th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th></tr></thead>
                          <tbody>{items.map((e,i)=><tr key={e.expense_id||i}><td className="td-num">{i+1}</td><td>{fmtDate(e.expense_date)}</td><td>{e.title}</td><td className="td-amount red">{fmtAmt(e.amount)}</td></tr>)}</tbody>
                          <tfoot><tr><td colSpan="3" style={{textAlign:"right",padding:"8px 14px"}}>Subtotal</td><td className="tfoot-amt" style={{padding:"8px 14px",textAlign:"right",color:"#f5b8b8"}}>{fmtAmt(catTotal)}</td></tr></tfoot>
                        </table>
                      </div>
                    );
                  })}
                  {expenses.length === 0 && <div style={{padding:"16px",color:"#b8a070",fontStyle:"italic",textAlign:"center"}}>No expenses for this period.</div>}
                </div>

                {/* Section 4: Financial Summary */}
                <div className="pdf-section">
                  <div className="pdf-section-header">
                    <div className="pdf-sec-accent" style={{background:netBalance>=0?"linear-gradient(180deg,#2a6b2a,#5a9a5a)":"linear-gradient(180deg,#8b1a1a,#c94040)"}} />
                    <div className="pdf-sec-title-block">
                      <div className="pdf-sec-title">{t("rptPdfSection4")||"4 · Financial Summary"}</div>
                      <div className="pdf-sec-subtitle">{t("rptPdfPeriod")||"Period"}: {periodLabel}</div>
                    </div>
                  </div>
                  <div className="pdf-fin-summary">
                    <div className="pdf-fs-row"><div className="pdf-fs-label"><span className="dot" style={{background:"#2a6b2a"}} />{t("rptPdfIncomeFromDonations")||"Income from Donations"} <span style={{fontSize:11,color:"#b8a070",marginLeft:6}}>({donations.length} records)</span></div><div className="pdf-fs-val green">{fmtAmt(totalDonations)}</div></div>
                    <div className="pdf-fs-row"><div className="pdf-fs-label"><span className="dot" style={{background:"#5a9a2a"}} />{t("rptPdfIncomeFromOfferings")||"Income from Offerings"} <span style={{fontSize:11,color:"#b8a070",marginLeft:6}}>({bookings.length} bookings)</span></div><div className="pdf-fs-val green">{fmtAmt(totalBookings)}</div></div>
                    <div className="pdf-fs-row gross"><div className="pdf-fs-label">{t("rptPdfGrossIncome")||"GROSS INCOME"}</div><div className="pdf-fs-val green">{fmtAmt(totalIncome)}</div></div>
                    <div className="pdf-fs-row"><div className="pdf-fs-label"><span className="dot" style={{background:"#8b1a1a"}} />{t("rptPdfTotalExpenses")||"Total Expenses"} <span style={{fontSize:11,color:"#b8a070",marginLeft:6}}>({expenses.length} entries)</span></div><div className="pdf-fs-val red">{fmtAmt(totalExpenses)}</div></div>
                    <div className={`pdf-fs-row ${netBalance>=0?"net-pos":"net-neg"}`}>
                      <div className="pdf-fs-label">{t("rptPdfNetBalance")||"NET BALANCE"} {netBalance>=0?`(${t("rptPdfSurplus")||"SURPLUS"})`:`(${t("rptPdfDeficit")||"DEFICIT"})`}</div>
                      <div className={`pdf-fs-val ${netBalance>=0?"green":"red"}`}>{fmtAmt(Math.abs(netBalance))}</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="pdf-footer">
                  <div className="pdf-footer-text">{t("rptPdfFooterNote")||"This is a computer-generated financial statement."}<br />{t("rptPdfFooterNote2")||"All amounts are in Indian Rupees (INR). Report period:"} {periodLabel}.</div>
                  <div style={{textAlign:"center"}}><div className="pdf-footer-om">ॐ</div><div className="pdf-footer-seal-text">{t("rptPdfTempleRecords")||"Temple Records"}</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN — Reports (Public)
══════════════════════════════════════════════════════════ */
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function Reports() {
  const { t, i18n } = useTranslation();
  const [authChecked, setAuthChecked] = useState(false);

  const [donations,         setDonations]         = useState([]);
  const [bookings,          setBookings]           = useState([]);
  const [expenses,          setExpenses]           = useState([]);
  const [expenseCategories, setExpenseCategories]  = useState([]);
  const [loading,           setLoading]            = useState(true);
  const [logoFace,          setLogoFace]           = useState("om");
  const [activeTab,         setActiveTab]          = useState("all");
  const [toast,             setToast]              = useState(null);
  const [pdfModal,          setPdfModal]           = useState(false);
  const [showFilter,        setShowFilter]         = useState(false);

  // Shared date filters
  const [filterMode,     setFilterMode]     = useState("month");
  const [selectedMonth,  setSelectedMonth]  = useState(() => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`; });
  const [selectedYear,   setSelectedYear]   = useState(String(new Date().getFullYear()));
  const [specificDate,   setSpecificDate]   = useState("");
  const [dateRange,      setDateRange]      = useState({ start: "", end: "" });
  const [sourceFilter,   setSourceFilter]   = useState("all");
  const [donTypeFilter,  setDonTypeFilter]  = useState("all");
  const [expCatFilter,   setExpCatFilter]   = useState("all");

  const showToast = (msg, type="info") => setToast({ message: msg, type });

  // Medallion flip
  useEffect(() => {
    const id = setInterval(() => setLogoFace(f => f==="om"?"logo":"om"), 3000);
    return () => clearInterval(id);
  }, []);

  // ── FETCH ──
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, bRes, eRes, ecRes] = await Promise.all([
        fetch(`${API}/api/public/donations`),
        fetch(`${API}/api/public/bookings`),
        fetch(`${API}/api/public/expenses`),
        fetch(`${API}/api/public/expense-categories`),
      ]);
      const [d, b, e, ec] = await Promise.all([dRes.json(), bRes.json(), eRes.json(), ecRes.json()]);
      setDonations(Array.isArray(d) ? d : []);
      setBookings(Array.isArray(b) ? b : []);
      setExpenses(Array.isArray(e) ? e : []);
      setExpenseCategories(Array.isArray(ec) ? ec : []);
    } catch {
      showToast(t("rptLoading")||"Failed to load report data.", "error");
    } finally {
      setLoading(false);
    }
  }, [API, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // If somehow an admin lands on /home, redirect them to /admin
        if (parsedUser.role === 'admin') {
          window.location.href = '/admin';
          return;
        }

        setAuthChecked(true); // ✅ valid devotee — show the page

      } catch (e) {
        localStorage.clear();
        window.location.href = '/login';
      }
    } else {
      // No session — send to login
      window.location.href = '/login';
    }
  }, []);


  // ── DATE HELPER ──
  const toDateStr = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (!s.endsWith("Z")) return s.slice(0,10);
    const d = new Date(s);
    return isNaN(d) ? s.slice(0,10) : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };

  const inDateFilter = (dateVal) => {
    const ds = toDateStr(dateVal);
    if (!ds) return false;
    if (filterMode==="specificDate" && specificDate) return ds===specificDate;
    if (filterMode==="month"  && selectedMonth) return ds.startsWith(selectedMonth);
    if (filterMode==="year"   && selectedYear)  return ds.startsWith(selectedYear);
    if (filterMode==="dateRange") {
      if (dateRange.start && dateRange.end) return ds>=dateRange.start && ds<=dateRange.end;
      if (dateRange.start) return ds>=dateRange.start;
      if (dateRange.end)   return ds<=dateRange.end;
      return true;
    }
    return true;
  };

  // ── FILTERED SETS ──
  const filteredDonations = donations.filter(d => {
    if (!inDateFilter(d.created_at)) return false;
    if (donTypeFilter!=="all" && d.donation_type!==donTypeFilter) return false;
    if (sourceFilter==="online"  && d.booked_by!=="online") return false;
    if (sourceFilter==="offline" && d.booked_by!=="admin")  return false;
    return d.payment_status==="success";
  });

  const filteredBookings = bookings.filter(b => {
    if (!inDateFilter(b.booking_date||b.created_at)) return false;
    if (sourceFilter==="online"  && b.booked_by!=="online") return false;
    if (sourceFilter==="offline" && b.booked_by!=="admin")  return false;
    return b.payment_status==="success";
  });

  const filteredExpenses = expenses.filter(e => {
    if (!inDateFilter(e.expense_date||e.created_at)) return false;
    if (expCatFilter!=="all" && String(e.category_id)!==String(expCatFilter)) return false;
    return true;
  });

  // ── TOTALS ──
  const totalDonations = filteredDonations.reduce((s,d)=>s+parseFloat(d.amount||0),0);
  const totalBookings  = filteredBookings.reduce((s,b)=>s+parseFloat(b.amount||0),0);
  const totalIncome    = totalDonations + totalBookings;
  const totalExpenses  = filteredExpenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const netBalance     = totalIncome - totalExpenses;

  const fmtAmt  = (n) => `₹${parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2})}`;
  const fmtDate = (v) => { if(!v)return"—"; const d=new Date(v); return isNaN(d)?v:d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); };

  const YEARS = Array.from({length:6},(_,i)=>String(new Date().getFullYear()-i));
  const donationTypes = [...new Set(donations.map(d=>d.donation_type).filter(Boolean))];

  const activeFilterCount =
    (filterMode!=="month" ? 1 : 0) +
    (donTypeFilter!=="all" ? 1 : 0) +
    (expCatFilter!=="all" ? 1 : 0) +
    (sourceFilter!=="all" ? 1 : 0);

  const clearFilters = () => {
    setFilterMode("month");
    const n=new Date(); setSelectedMonth(`${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`);
    setSelectedYear(String(n.getFullYear())); setSpecificDate(""); setDateRange({start:"",end:""});
    setDonTypeFilter("all"); setExpCatFilter("all"); setSourceFilter("all");
  };

  const periodLabel = (() => {
    if (filterMode==="specificDate" && specificDate) return new Date(specificDate).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"});
    if (filterMode==="month" && selectedMonth) { const [y,m]=selectedMonth.split("-"); return new Date(y,m-1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); }
    if (filterMode==="year") return `Year ${selectedYear}`;
    if (filterMode==="dateRange") { const s=dateRange.start?new Date(dateRange.start).toLocaleDateString("en-IN"):"—"; const e=dateRange.end?new Date(dateRange.end).toLocaleDateString("en-IN"):"—"; return `${s} → ${e}`; }
    return t("rptFilterAllTime")||"All Time";
  })();

  // Stats — all 4 always shown, active tab highlighted
  const statData = [
    { val: fmtAmt(totalDonations), lbl: t("rptStatDonations")||"Donations",   color: "#2a6b2a", active: activeTab==="donations" },
    { val: fmtAmt(totalBookings),  lbl: t("rptStatOfferings")||"Offerings",   color: "#c9973a", active: activeTab==="offerings" },
    { val: fmtAmt(totalExpenses),  lbl: t("rptStatTotalExpenses")||"Expenses", color: "#8b1a1a", active: activeTab==="expenses" },
    { val: fmtAmt(netBalance),     lbl: t("rptStatNetBalance")||"Net Balance", color: netBalance>=0?"#1a5a2a":"#8b1a1a", active: activeTab==="all" },
  ];
  if (!authChecked) return null;

  return (
    <>
      <RptStyles />
      <Navbar />

      {/* ══ HERO ══ */}
      <div className="rpt-hero">
        <div className="rpt-hero-geo rpt-geo-1" />
        <div className="rpt-hero-geo rpt-geo-2" />
        <div className="rpt-hero-geo rpt-geo-3" />
        <div className="rpt-hero-scanlines" />

        {/* Side columns */}
        <div className="rpt-hero-side">
          <div className="rpt-side-ornament top">{[...Array(7)].map((_,i)=><div key={i} className="rpt-side-tick" style={{opacity:0.15+i*0.1,height:3+i*2}} />)}</div>
          <div className="rpt-side-om">ॐ</div>
          <div className="rpt-side-line" />
          <span className="rpt-side-sym">◈</span>
          <div className="rpt-side-line" />
          <div className="rpt-side-ornament bot">{[...Array(7)].map((_,i)=><div key={i} className="rpt-side-tick" style={{opacity:0.85-i*0.1,height:3+(6-i)*2}} />)}</div>
        </div>

        {/* Center */}
        <div className="rpt-hero-center">
          {/* Flipping medallion */}
          <div className="rpt-hero-medallion">
            <div className="rpt-hero-med-ring" />
            <div className="rpt-hero-med-ring rpt-hero-med-ring-2" />
            <div className={`rpt-hero-face ${logoFace === "om" ? "visible" : ""}`}>
              <span className="rpt-hero-om">ॐ</span>
            </div>
            <div className={`rpt-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
              <img src="./logo.png" alt="Temple" className="rpt-hero-logo-img" />
            </div>
            <div className="rpt-hero-med-halo" />
          </div>

          <div className="rpt-hero-eyebrow"><span className="rpt-ey-dash"/><span className="rpt-ey-text">✦ {t("rptTempleRecords")||"Temple Records"} ✦</span><span className="rpt-ey-dash"/></div>
          <h1 className="rpt-hero-title">{t("rptPageTitle")||"Financial Reports"}</h1>
          <p className="rpt-hero-sub">{t("rptPageSub")||"Temple income, expenses & financial transparency"}</p>

          {/* Period chip */}
          {!loading && <div className="rpt-period-chip"><FaCalendarAlt className="rpt-period-icon" /> {periodLabel}</div>}

          {/* Tab selector */}
          <div className="rpt-tab-selector">
            {[
              { key:"all",       label: t("rptTabAll")||"All",         icon:<FaBalanceScale />,      count: filteredDonations.length + filteredBookings.length + filteredExpenses.length },
              { key:"donations", label: t("rptSecDonations")||"Donations",  icon:<FaHandHoldingHeart />, count: filteredDonations.length },
              { key:"offerings", label: t("rptSecOfferings")||"Offerings",  icon:<FaPray />,             count: filteredBookings.length },
              { key:"expenses",  label: t("rptSecExpenses")||"Expenses",    icon:<FaMoneyBillWave />,    count: filteredExpenses.length },
            ].map(tb=>(
              <button key={tb.key} className={`rpt-tab-btn ${activeTab===tb.key?"active":""}`} onClick={()=>setActiveTab(tb.key)}>
                <span className="rpt-tab-icon">{tb.icon}</span>
                <span className="rpt-tab-label">{tb.label}</span>
                {tb.count > 0 && <span className="rpt-tab-count">{tb.count}</span>}
              </button>
            ))}
          </div>

          {/* Stat row */}
          <div className="rpt-stat-row" key={activeTab}>
            {statData.map((s,i)=>(
              <div key={i} className={`rpt-stat ${s.active?"rpt-stat-active":""}`}>
                <span className="rpt-stat-val rpt-stat-animate" style={{"--sv":s.color,animationDelay:`${i*0.07}s`}}>{s.val}</span>
                <span className="rpt-stat-lbl">{s.lbl}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right side col */}
        <div className="rpt-hero-side">
          <div className="rpt-side-ornament top">{[...Array(7)].map((_,i)=><div key={i} className="rpt-side-tick" style={{opacity:0.15+i*0.1,height:3+i*2}} />)}</div>
          <div className="rpt-side-om">ॐ</div>
          <div className="rpt-side-line" />
          <span className="rpt-side-sym">◈</span>
          <div className="rpt-side-line" />
          <div className="rpt-side-ornament bot">{[...Array(7)].map((_,i)=><div key={i} className="rpt-side-tick" style={{opacity:0.85-i*0.1,height:3+(6-i)*2}} />)}</div>
        </div>

        <div className="rpt-hero-bottom-strip" />
      </div>

      {/* ══ TOOLBAR ══ */}
      <div className="rpt-toolbar">
        <button className="rpt-filter-btn" onClick={()=>setShowFilter(v=>!v)}>
          <FaFilter />
          <span>{t("btnFilters")||"Filters"}</span>
          {activeFilterCount>0 && <span className="rpt-filter-badge">{activeFilterCount}</span>}
          <FaChevronDown className={`rpt-chevron ${showFilter?"open":""}`} />
        </button>
        <div className="rpt-toolbar-end">
          {activeFilterCount>0 && <button className="rpt-action-btn rpt-btn-clear" onClick={clearFilters}><FaTimes /> {t("btnClear")||"Clear"}</button>}
          <button className="rpt-action-btn rpt-btn-pdf" onClick={()=>setPdfModal(true)} disabled={loading}><FaFilePdf /> {t("rptBtnPreviewPdf")||"PDF"}</button>
          <button className="rpt-action-btn rpt-btn-refresh" onClick={fetchAll}><FaSync className={loading?"spin":""} /> {t("btnRefresh")||"Refresh"}</button>
        </div>
      </div>

      {/* ══ FILTER PANEL ══ */}
      {showFilter && (
        <div className="rpt-filter-panel">
          <div className="rpt-fp-strip" />
          <div className="rpt-filter-grid">

            {/* Period mode tabs */}
            <div className="rpt-fi rpt-fi-full">
              <label className="rpt-fi-label"><FaCalendarAlt /> {t("rptFilterPeriod")||"Period"}</label>
              <div className="rpt-mode-tabs">
                {[
                  {key:"month",        label:t("rptFilterByMonth")||"By Month"},
                  {key:"year",         label:t("rptFilterByYear")||"By Year"},
                  {key:"dateRange",    label:t("rptFilterDateRange")||"Date Range"},
                  {key:"specificDate", label:t("rptFilterSpecificDate")||"Specific Date"},
                  {key:"all",          label:t("rptFilterAllTime")||"All Time"},
                ].map(({key,label})=>(
                  <button key={key} className={`rpt-mode-tab ${filterMode===key?"active":""}`} onClick={()=>setFilterMode(key)}>{label}</button>
                ))}
              </div>
            </div>

            {/* Period input */}
            {filterMode==="month" && (
              <div className="rpt-fi">
                <label className="rpt-fi-label">{t("rptFilterByMonth")||"Month"}</label>
                <input type="month" className="rpt-fi-input" value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)} />
              </div>
            )}
            {filterMode==="year" && (
              <div className="rpt-fi">
                <label className="rpt-fi-label">{t("rptFilterByYear")||"Year"}</label>
                <select className="rpt-fi-select" value={selectedYear} onChange={e=>setSelectedYear(e.target.value)}>
                  {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            {filterMode==="specificDate" && (
              <div className="rpt-fi">
                <label className="rpt-fi-label">{t("rptFilterSpecificDate")||"Specific Date"}</label>
                <input type="date" className="rpt-fi-input" value={specificDate} onChange={e=>setSpecificDate(e.target.value)} />
              </div>
            )}
            {filterMode==="dateRange" && (
              <div className="rpt-fi rpt-fi-range">
                <label className="rpt-fi-label">{t("rptFilterDateRange")||"Date Range"}</label>
                <div className="rpt-range-row">
                  <input type="date" className="rpt-fi-input" value={dateRange.start} onChange={e=>setDateRange(p=>({...p,start:e.target.value}))} />
                  <span className="rpt-range-sep">→</span>
                  <input type="date" className="rpt-fi-input" value={dateRange.end} onChange={e=>setDateRange(p=>({...p,end:e.target.value}))} />
                </div>
              </div>
            )}

            {/* Donation type */}
            <div className="rpt-fi">
              <label className="rpt-fi-label"><FaHandHoldingHeart /> {t("rptFilterDonationType")||"Donation Type"}</label>
              <select className="rpt-fi-select" value={donTypeFilter} onChange={e=>setDonTypeFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllTypes")||"All Types"}</option>
                {donationTypes.map(tp=><option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>

            {/* Expense category */}
            <div className="rpt-fi">
              <label className="rpt-fi-label"><FaTags /> {t("rptFilterExpCategory")||"Expense Category"}</label>
              <select className="rpt-fi-select" value={expCatFilter} onChange={e=>setExpCatFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllCategories")||"All Categories"}</option>
                {expenseCategories.map(c=><option key={c.category_id} value={c.category_id}>{c.title}</option>)}
              </select>
            </div>

            {/* Source */}
            <div className="rpt-fi">
              <label className="rpt-fi-label"><FaGlobe /> {t("rptFilterSource")||"Source"}</label>
              <select className="rpt-fi-select" value={sourceFilter} onChange={e=>setSourceFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllSources")||"All Sources"}</option>
                <option value="online">{t("rptFilterOnline")||"Online"}</option>
                <option value="offline">{t("rptFilterCounter")||"Counter"}</option>
              </select>
            </div>

          </div>
        </div>
      )}

      {/* ══ RESULT INFO ══ */}
      <div className="rpt-result-info">
        {!loading && (
          <span>
            {t("displaying")||"Showing"}{" "}
            <strong>
              {activeTab==="all"
                ? filteredDonations.length + filteredBookings.length + filteredExpenses.length
                : activeTab==="donations" ? filteredDonations.length
                : activeTab==="offerings" ? filteredBookings.length
                : filteredExpenses.length}
            </strong> record(s)
            {activeTab==="all" && <> &nbsp;·&nbsp; Net: <strong style={{color:"var(--gold-dim)"}}>{fmtAmt(netBalance)}</strong></>}
            {activeFilterCount>0 && <button className="rpt-clear-inline" onClick={clearFilters}><FaTimes /> {t("clearAll")||"Clear all"}</button>}
          </span>
        )}
      </div>

      {/* ══ BODY ══ */}
      <div className="rpt-body">
        {loading ? (
          <div className="rpt-loader">
            <div className="rpt-loader-ring-wrap">
              <div className="rpt-loader-ring" />
              <div className="rpt-loader-ring-inner" />
              <div className="rpt-loader-om">ॐ</div>
            </div>
            <p>{t("rptLoading")||"Loading report data…"}</p>
          </div>
        ) : filteredDonations.length + filteredBookings.length + filteredExpenses.length === 0 ? (
          <div className="rpt-empty rpt-empty-full">{t("rptNoRecords")||"No records found for the selected period and filters."}</div>
        ) : (
          <>
            {/* ── DONATIONS SECTION — shown on "all" or "donations" tab ── */}
            {(activeTab==="all" || activeTab==="donations") && (
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <div className="rpt-sec-icon-wrap" style={{background:"rgba(42,107,42,0.1)",border:"1px solid rgba(42,107,42,0.25)"}}><FaHandHoldingHeart style={{color:"#2a6b2a"}} /></div>
                  <div className="rpt-sec-title-area">
                    <div className="rpt-sec-title">{t("rptSecDonations")||"Donations / Sambhavana"}</div>
                    <div className="rpt-sec-meta">{filteredDonations.length} records &nbsp;·&nbsp; <strong style={{color:"#2a6b2a"}}>{fmtAmt(totalDonations)}</strong></div>
                  </div>
                </div>
                {filteredDonations.length===0 ? (
                  <div className="rpt-empty">{t("rptNoDonations")||"No donation records found for this period."}</div>
                ) : (
                  <div className="rpt-table-wrap">
                    {/* Desktop table */}
                    <table className="rpt-table rpt-desk-only">
                      <thead><tr>
                        <th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColReceipt")||"Receipt"}</th>
                        <th>{t("rptColDonorName")||"Donor"}</th><th>{t("rptColType")||"Type"}</th>
                        <th>{t("rptColSource")||"Source"}</th><th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th>
                      </tr></thead>
                      <tbody>
                        {filteredDonations.map((d,i)=>(
                          <tr key={d.donation_id||i} className="rpt-tr">
                            <td className="rpt-td-num">{i+1}</td>
                            <td>{fmtDate(d.created_at)}</td>
                            <td className="rpt-td-receipt">{d.receipt_number||"—"}</td>
                            <td className="rpt-td-name">{d.name||"—"}</td>
                            <td><span className="rpt-pill">{d.donation_type||"—"}</span></td>
                            <td><span className={`rpt-src-pill ${d.booked_by==="online"?"online":"counter"}`}>{d.booked_by==="online"?(t("rptSourceOnline")||"Online"):(t("rptSourceCounter")||"Counter")}</span></td>
                            <td className="rpt-td-amount">{fmtAmt(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan="6" className="rpt-tfoot-label">{t("rptTotalDonations")||"Total Donations"}</td><td className="rpt-td-amount rpt-tfoot-val">{fmtAmt(totalDonations)}</td></tr></tfoot>
                    </table>
                    {/* Mobile cards */}
                    <div className="rpt-mob-only">
                      {filteredDonations.map((d,i)=>(
                        <div key={d.donation_id||i} className="rpt-mob-card">
                          <div className="rpt-mob-header">
                            <span className="rpt-mob-num">#{i+1}</span>
                            <span className={`rpt-src-pill ${d.booked_by==="online"?"online":"counter"}`}>{d.booked_by==="online"?"Online":"Counter"}</span>
                          </div>
                          <div className="rpt-mob-name">{d.name||"—"}</div>
                          <div className="rpt-mob-meta">
                            <span><FaCalendarAlt className="rpt-mob-ic" />{fmtDate(d.created_at)}</span>
                            <span className="rpt-pill">{d.donation_type||"—"}</span>
                          </div>
                          <div className="rpt-mob-meta">
                            <span className="rpt-td-receipt">{d.receipt_number||"—"}</span>
                            <span className="rpt-mob-amount">{fmtAmt(d.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── OFFERINGS SECTION — shown on "all" or "offerings" tab ── */}
            {(activeTab==="all" || activeTab==="offerings") && (
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <div className="rpt-sec-icon-wrap" style={{background:"rgba(138,96,32,0.1)",border:"1px solid rgba(138,96,32,0.25)"}}><FaPray style={{color:"#8a6020"}} /></div>
                  <div className="rpt-sec-title-area">
                    <div className="rpt-sec-title">{t("rptSecOfferings")||"Vazhipadu / Offerings"}</div>
                    <div className="rpt-sec-meta">{filteredBookings.length} records &nbsp;·&nbsp; <strong style={{color:"#8a6020"}}>{fmtAmt(totalBookings)}</strong></div>
                  </div>
                </div>
                {filteredBookings.length===0 ? (
                  <div className="rpt-empty">{t("rptNoOfferings")||"No offering records found for this period."}</div>
                ) : (
                  <div className="rpt-table-wrap">
                    <table className="rpt-table rpt-desk-only">
                      <thead><tr>
                        <th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColReceipt")||"Receipt"}</th>
                        <th>{t("rptColVazhipadu")||"Vazhipadu"}</th><th style={{textAlign:"center"}}>{t("rptColPersons")||"Persons"}</th>
                        <th>{t("rptColSource")||"Source"}</th><th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th>
                      </tr></thead>
                      <tbody>
                        {filteredBookings.map((b,i)=>(
                          <tr key={b.booking_id||i} className="rpt-tr">
                            <td className="rpt-td-num">{i+1}</td>
                            <td>{fmtDate(b.booking_date||b.created_at)}</td>
                            <td className="rpt-td-receipt">{b.receipt_number||"—"}</td>
                            <td><span className="rpt-pill">{b.category_name||"—"}</span></td>
                            <td style={{textAlign:"center"}}>{b.total_persons||1}</td>
                            <td><span className={`rpt-src-pill ${b.booked_by==="online"?"online":"counter"}`}>{b.booked_by==="online"?(t("rptSourceOnline")||"Online"):(t("rptSourceCounter")||"Counter")}</span></td>
                            <td className="rpt-td-amount">{fmtAmt(b.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan="6" className="rpt-tfoot-label">{t("rptTotalOfferings")||"Total Offerings"}</td><td className="rpt-td-amount rpt-tfoot-val">{fmtAmt(totalBookings)}</td></tr></tfoot>
                    </table>
                    <div className="rpt-mob-only">
                      {filteredBookings.map((b,i)=>(
                        <div key={b.booking_id||i} className="rpt-mob-card">
                          <div className="rpt-mob-header">
                            <span className="rpt-mob-num">#{i+1}</span>
                            <span className={`rpt-src-pill ${b.booked_by==="online"?"online":"counter"}`}>{b.booked_by==="online"?"Online":"Counter"}</span>
                          </div>
                          <div className="rpt-mob-name"><span className="rpt-pill">{b.category_name||"—"}</span></div>
                          <div className="rpt-mob-meta">
                            <span><FaCalendarAlt className="rpt-mob-ic"/>{fmtDate(b.booking_date||b.created_at)}</span>
                            <span>{t("rptColPersons")||"Persons"}: {b.total_persons||1}</span>
                          </div>
                          <div className="rpt-mob-meta">
                            <span className="rpt-td-receipt">{b.receipt_number||"—"}</span>
                            <span className="rpt-mob-amount">{fmtAmt(b.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── EXPENSES SECTION — shown on "all" or "expenses" tab ── */}
            {(activeTab==="all" || activeTab==="expenses") && (
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <div className="rpt-sec-icon-wrap" style={{background:"rgba(139,26,26,0.1)",border:"1px solid rgba(139,26,26,0.25)"}}><FaMoneyBillWave style={{color:"#8b1a1a"}} /></div>
                  <div className="rpt-sec-title-area">
                    <div className="rpt-sec-title">{t("rptSecExpenses")||"Temple Expenses"}</div>
                    <div className="rpt-sec-meta">{filteredExpenses.length} entries &nbsp;·&nbsp; <strong style={{color:"#8b1a1a"}}>{fmtAmt(totalExpenses)}</strong></div>
                  </div>
                </div>
                {filteredExpenses.length===0 ? (
                  <div className="rpt-empty">{t("rptNoExpenses")||"No expense records found for this period."}</div>
                ) : (
                  <div className="rpt-table-wrap">
                    <table className="rpt-table rpt-desk-only">
                      <thead><tr>
                        <th>#</th><th>{t("rptColDate")||"Date"}</th><th>{t("rptColCategory")||"Category"}</th>
                        <th>{t("rptColExpType")||"Type"}</th><th>{t("rptColTitle")||"Title"}</th>
                        <th style={{textAlign:"right"}}>{t("rptColAmount")||"Amount"}</th>
                      </tr></thead>
                      <tbody>
                        {filteredExpenses.map((e,i)=>(
                          <tr key={e.expense_id||i} className="rpt-tr">
                            <td className="rpt-td-num">{i+1}</td>
                            <td>{fmtDate(e.expense_date)}</td>
                            <td><span className="rpt-pill">{e.category_title||"—"}</span></td>
                            <td><span className="rpt-pill rpt-pill-red">{e.category_type||"—"}</span></td>
                            <td className="rpt-td-name">{e.title}</td>
                            <td className="rpt-td-amount expense">{fmtAmt(e.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan="5" className="rpt-tfoot-label">{t("rptTotalExpenses")||"Total Expenses"}</td><td className="rpt-td-amount rpt-tfoot-val expense">{fmtAmt(totalExpenses)}</td></tr></tfoot>
                    </table>
                    <div className="rpt-mob-only">
                      {filteredExpenses.map((e,i)=>(
                        <div key={e.expense_id||i} className="rpt-mob-card rpt-mob-card-expense">
                          <div className="rpt-mob-header">
                            <span className="rpt-mob-num">#{i+1}</span>
                            <span className="rpt-pill">{e.category_title||"—"}</span>
                          </div>
                          <div className="rpt-mob-name">{e.title}</div>
                          <div className="rpt-mob-meta">
                            <span><FaCalendarAlt className="rpt-mob-ic"/>{fmtDate(e.expense_date)}</span>
                            <span className="rpt-mob-amount expense">{fmtAmt(e.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── FINANCIAL SUMMARY (always shown below the tab) ── */}
            <div className="rpt-summary-section">
              <div className="rpt-section-header rpt-summary-header">
                <div className="rpt-sec-icon-wrap" style={{background:netBalance>=0?"rgba(26,90,42,0.15)":"rgba(139,26,26,0.15)",border:`1px solid ${netBalance>=0?"rgba(26,90,42,0.3)":"rgba(139,26,26,0.3)"}`}}>
                  <FaBalanceScale style={{color:netBalance>=0?"#1a5a2a":"#8b1a1a"}} />
                </div>
                <div className="rpt-sec-title-area">
                  <div className="rpt-sec-title" style={{color:"var(--gold-bright)"}}>{t("rptSecSummary")||"Financial Summary"}</div>
                  <div className="rpt-sec-meta" style={{color:"rgba(232,196,106,0.5)"}}>{periodLabel}</div>
                </div>
              </div>
              <div className="rpt-sumgrid">
                <div className="rpt-sumrow"><div className="rpt-sumrow-label"><FaHandHoldingHeart /> {t("rptSummaryDonIncome")||"Donations Income"}</div><div className="rpt-sumrow-val" style={{color:"#2a6b2a"}}>{fmtAmt(totalDonations)}</div></div>
                <div className="rpt-sumrow"><div className="rpt-sumrow-label"><FaPray /> {t("rptSummaryOfferIncome")||"Offerings Income"}</div><div className="rpt-sumrow-val" style={{color:"#2a6b2a"}}>{fmtAmt(totalBookings)}</div></div>
                <div className="rpt-sumrow rpt-sumrow-gross"><div className="rpt-sumrow-label"><FaArrowUp /> {t("rptSummaryGrossIncome")||"GROSS INCOME"}</div><div className="rpt-sumrow-val" style={{color:"#1a4a1a",fontSize:"18px"}}>{fmtAmt(totalIncome)}</div></div>
                <div className="rpt-sumrow"><div className="rpt-sumrow-label"><FaArrowDown /> {t("rptTotalExpenses")||"Total Expenses"}</div><div className="rpt-sumrow-val" style={{color:"#8b1a1a"}}>{fmtAmt(totalExpenses)}</div></div>
                <div className={`rpt-sumrow rpt-sumrow-balance ${netBalance>=0?"pos":"neg"}`}>
                  <div className="rpt-sumrow-label"><FaBalanceScale /> {t("rptSummaryNetBalance")||"NET BALANCE"}</div>
                  <div className="rpt-sumrow-val" style={{color:netBalance>=0?"#1a4a1a":"#8b1a1a",fontSize:"22px"}}>{fmtAmt(netBalance)}</div>
                </div>
              </div>
              <div className="rpt-summary-foot">
                <button className="rpt-pdf-big-btn" onClick={()=>setPdfModal(true)}>
                  <span className="rpt-pdf-btn-shimmer" />
                  <FaFilePdf style={{position:"relative",zIndex:1}} />
                  <span style={{position:"relative",zIndex:1}}>{t("rptBtnPreviewDownload")||"Preview & Download PDF Report"}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══ PDF MODAL ══ */}
      {pdfModal && (
        <PdfPreviewModal
          reportData={{ donations: filteredDonations, bookings: filteredBookings, expenses: filteredExpenses }}
          filters={{ month: filterMode==="month"?selectedMonth:null, year: filterMode==="year"?selectedYear:null, specificDate: filterMode==="specificDate"?specificDate:null, dateRange: filterMode==="dateRange"?dateRange:null }}
          onClose={()=>setPdfModal(false)}
          t={t} i18n={i18n}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)} />}
      <Footer />
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════════════════ */
function RptStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-light: #3d0c0c;
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --parchment: #FDF3E3; --text-dark: #1a0404; --text-mid: #4a1a08;
        --green: #2a6b2a; --red: #8b1a1a;
      }

      /* ════ HERO ════ */
      .rpt-hero {
        display: grid; grid-template-columns: 68px 1fr 68px;
        background: linear-gradient(160deg, #2e0c0c 0%, #1c0505 50%, #100202 100%);
        position: relative; overflow: hidden; border-bottom: 1px solid rgba(201,151,58,0.18);
      }
      .rpt-hero-geo { position: absolute; border-radius: 50%; pointer-events: none; }
      .rpt-geo-1 { width: 600px; height: 600px; top: -300px; right: -150px; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%); }
      .rpt-geo-2 { width: 400px; height: 400px; bottom: -200px; left: -100px; background: radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .rpt-geo-3 { width: 200px; height: 200px; top: 20px; left: 20%; background: radial-gradient(ellipse, rgba(201,151,58,0.06) 0%, transparent 70%); }
      .rpt-hero-scanlines { position: absolute; inset: 0; background: repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px); pointer-events: none; }

      /* Medallion */
      .rpt-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,255,255,0.06); }
      .rpt-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: mbSlowSpin 10s linear infinite; }
      .rpt-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      .rpt-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .rpt-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .rpt-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .rpt-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .rpt-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .rpt-hero-bottom-strip { position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, #8a6020 20%, #e8c46a 50%, #8a6020 80%, transparent 100%); grid-column: 1/-1; }

      /* Side columns */
      .rpt-hero-side { display: flex; flex-direction: column; align-items: center; padding: 32px 0 28px; gap: 8px; position: relative; z-index: 1; }
      .rpt-side-ornament { display: flex; flex-direction: column; align-items: center; gap: 3px; }
      .rpt-side-tick { width: 2px; background: linear-gradient(180deg, transparent, var(--gold-dim), transparent); border-radius: 1px; }
      .rpt-side-om { font-family: 'Cinzel Decorative', cursive; font-size: 16px; color: rgba(232,196,106,0.45); text-shadow: 0 0 12px rgba(201,151,58,0.3); }
      .rpt-side-line { flex: 1; width: 1px; background: linear-gradient(180deg, transparent, rgba(201,151,58,0.2), transparent); min-height: 20px; }
      .rpt-side-sym { font-size: 14px; color: rgba(232,196,106,0.3); }

      /* Hero center */
      .rpt-hero-center { padding: 40px 24px 32px; display: flex; flex-direction: column; align-items: center; text-align: center; position: relative; z-index: 1; }
      .rpt-hero-eyebrow { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
      .rpt-ey-dash { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35)); max-width: 60px; }
      .rpt-ey-text { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(232,196,106,0.55); }
      .rpt-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 30px; font-weight: 700; color: var(--gold-bright); letter-spacing: 2px; line-height: 1.2; margin-bottom: 10px; text-shadow: 0 2px 24px rgba(201,151,58,0.25); }
      .rpt-hero-sub { font-family: 'EB Garamond', serif; font-size: 16px; font-style: italic; color: rgba(232,196,106,0.48); margin-bottom: 20px; }
      .rpt-period-chip { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.7); background: rgba(201,151,58,0.09); border: 1px solid rgba(201,151,58,0.22); border-radius: 20px; padding: 7px 16px; margin-bottom: 22px; }
      .rpt-period-icon { font-size: 9px; }

      /* Tab selector */
      .rpt-tab-selector { display: flex; gap: 8px; margin-bottom: 22px; width: 100%; max-width: 700px; }
      .rpt-tab-btn { position: relative; overflow: hidden; display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 12px 14px; background: rgba(255,255,255,0.03); border: 1px solid rgba(201,151,58,0.2); color: rgba(232,196,106,0.45); cursor: pointer; border-radius: 3px; transition: all 0.28s cubic-bezier(0.22,1,0.36,1); flex: 1; justify-content: center; }
      .rpt-tab-btn:hover { background: rgba(201,151,58,0.07); border-color: rgba(201,151,58,0.35); color: rgba(232,196,106,0.7); }
      .rpt-tab-btn.active { background: linear-gradient(135deg, rgba(201,151,58,0.18) 0%, rgba(232,196,106,0.11) 50%, rgba(201,151,58,0.18) 100%); border-color: rgba(201,151,58,0.6); color: #e8c46a; box-shadow: 0 4px 20px rgba(201,151,58,0.2), inset 0 1px 0 rgba(232,196,106,0.12); }
      .rpt-tab-btn.active::after { content: ''; position: absolute; bottom: 0; left: 15%; right: 15%; height: 2px; background: linear-gradient(90deg, transparent, var(--gold-bright), transparent); border-radius: 2px 2px 0 0; }
      .rpt-tab-icon { font-size: 12px; flex-shrink: 0; }
      .rpt-tab-label { white-space: nowrap; }
      .rpt-tab-count { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 900; background: rgba(201,151,58,0.22); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-bright); border-radius: 10px; padding: 2px 7px; letter-spacing: 0.5px; }

      /* Stat row */
      .rpt-stat-row { display: flex; align-items: stretch; border: 1px solid rgba(201,151,58,0.18); border-radius: 4px; overflow: hidden; width: 100%; max-width: 640px; box-shadow: 0 2px 16px rgba(0,0,0,0.15); }
      .rpt-stat { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 14px 12px; border-right: 1px solid rgba(201,151,58,0.12); transition: background 0.3s; }
      .rpt-stat:last-child { border-right: none; }
      .rpt-stat-active { background: rgba(201,151,58,0.09); }
      @keyframes rptStatPop { 0%{opacity:0;transform:translateY(5px) scale(0.88)} 60%{transform:translateY(-1px) scale(1.06)} 100%{opacity:1;transform:none} }
      .rpt-stat-animate { animation: rptStatPop 0.36s cubic-bezier(0.22,1,0.36,1) both; }
      .rpt-stat-val { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--sv, #e8c46a); letter-spacing: 0.5px; }
      .rpt-stat-lbl { font-family: 'Cinzel', serif; font-size: 7px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.45); margin-top: 5px; text-align: center; }

      /* ════ TOOLBAR ════ */
      .rpt-toolbar { background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); padding: 13px 32px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; box-shadow: 0 1px 8px rgba(26,4,4,0.06); }
      .rpt-filter-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.8px; text-transform: uppercase; padding: 9px 18px; background: rgba(201,151,58,0.07); border: 1px solid rgba(201,151,58,0.25); color: var(--gold-dim); cursor: pointer; border-radius: 3px; transition: all 0.2s; }
      .rpt-filter-btn:hover { background: rgba(201,151,58,0.13); border-color: rgba(201,151,58,0.4); color: var(--gold-mid); }
      .rpt-filter-badge { background: var(--gold-mid); color: var(--crimson-deep); font-size: 8px; font-weight: 900; padding: 2px 7px; border-radius: 10px; font-family: 'Cinzel', serif; }
      .rpt-chevron { font-size: 9px; transition: transform 0.25s; }
      .rpt-chevron.open { transform: rotate(180deg); }
      .rpt-toolbar-end { display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap; }
      .rpt-action-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 9px 16px; border: 1px solid; cursor: pointer; border-radius: 3px; transition: all 0.2s; clip-path: polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%); }
      .rpt-btn-pdf { background: rgba(139,26,26,0.07); border-color: rgba(139,26,26,0.3); color: #c04040; }
      .rpt-btn-pdf:hover { background: rgba(139,26,26,0.14); color: #e05050; }
      .rpt-btn-pdf:disabled { opacity: 0.5; cursor: not-allowed; }
      .rpt-btn-refresh { background: rgba(201,151,58,0.07); border-color: rgba(201,151,58,0.25); color: var(--gold-dim); }
      .rpt-btn-refresh:hover { background: rgba(201,151,58,0.14); color: var(--gold-mid); }
      .rpt-btn-clear { background: rgba(139,26,26,0.06); border-color: rgba(139,26,26,0.2); color: #a04040; }
      .rpt-btn-clear:hover { background: rgba(139,26,26,0.12); color: #c04040; }
      .spin { animation: mbSpin 1s linear infinite; }
      @keyframes mbSpin { to { transform: rotate(360deg); } }
      @keyframes mbSlowSpin { to { transform: rotate(360deg); } }

      /* ════ FILTER PANEL ════ */
      .rpt-filter-panel { background: #fffaf2; border-bottom: 1px solid rgba(201,151,58,0.15); position: relative; overflow: hidden; }
      .rpt-fp-strip { position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--gold-mid), transparent); }
      .rpt-filter-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; padding: 20px 32px 24px; }
      .rpt-fi { display: flex; flex-direction: column; gap: 6px; }
      .rpt-fi-full { grid-column: 1 / -1; }
      .rpt-fi-range { grid-column: span 2; }
      .rpt-fi-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); display: flex; align-items: center; gap: 6px; }
      .rpt-fi-input, .rpt-fi-select { font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-dark); background: #fff; border: 1px solid rgba(201,151,58,0.25); padding: 8px 10px; border-radius: 2px; outline: none; transition: border-color 0.2s; }
      .rpt-fi-input:focus, .rpt-fi-select:focus { border-color: rgba(201,151,58,0.55); }
      .rpt-range-row { display: flex; align-items: center; gap: 8px; }
      .rpt-range-sep { color: var(--gold-dim); font-size: 12px; }
      .rpt-mode-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
      .rpt-mode-tab { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 7px 14px; background: rgba(201,151,58,0.06); border: 1px solid rgba(201,151,58,0.2); color: var(--gold-dim); cursor: pointer; border-radius: 2px; transition: all 0.2s; clip-path: polygon(6px 0%,100% 0%,calc(100% - 6px) 100%,0% 100%); }
      .rpt-mode-tab:hover { background: rgba(201,151,58,0.12); border-color: rgba(201,151,58,0.35); }
      .rpt-mode-tab.active { background: linear-gradient(135deg, rgba(201,151,58,0.2), rgba(201,151,58,0.1)); border-color: rgba(201,151,58,0.5); color: var(--gold-mid); box-shadow: 0 2px 8px rgba(201,151,58,0.15); }

      /* ════ RESULT INFO ════ */
      .rpt-result-info { background: var(--parchment); padding: 8px 32px; border-bottom: 1px solid rgba(201,151,58,0.12); font-family: 'EB Garamond', serif; font-size: 14px; color: rgba(74,26,8,0.5); min-height: 36px; display: flex; align-items: center; }
      .rpt-result-info strong { color: var(--gold-dim); }
      .rpt-clear-inline { background: none; border: none; color: rgba(139,26,26,0.6); font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; margin-left: 12px; padding: 2px 6px; border-radius: 2px; transition: color 0.2s; }
      .rpt-clear-inline:hover { color: #8b1a1a; }

      /* ════ BODY ════ */
      .rpt-body { background: #fdf8ef; padding: 28px 32px 60px; min-height: 400px; }      .rpt-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 80px 0; }
      .rpt-loader-om { font-family: 'Cinzel Decorative', cursive; font-size: 48px; color: var(--gold-dim); animation: rptOmPulse 2s ease-in-out infinite alternate; }
      @keyframes rptOmPulse { from{opacity:0.3;transform:scale(0.95)} to{opacity:0.8;transform:scale(1.05)} }
      .rpt-loader p { font-family: 'EB Garamond', serif; font-size: 15px; font-style: italic; color: rgba(74,26,8,0.45); }

      /* Section */
      .rpt-section { background: #fff; border: 1px solid rgba(201,151,58,0.14); border-radius: 4px; margin-bottom: 22px; overflow: hidden; box-shadow: 0 2px 12px rgba(26,4,4,0.06); }
      .rpt-section-header { display: flex; align-items: center; gap: 14px; padding: 18px 22px; background: linear-gradient(90deg, rgba(201,151,58,0.05), transparent 60%); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .rpt-sec-icon-wrap { width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 17px; flex-shrink: 0; }
      .rpt-sec-title-area { flex: 1; }
      .rpt-sec-title { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--text-dark); }
      .rpt-sec-meta { font-family: 'EB Garamond', serif; font-size: 14px; color: rgba(74,26,8,0.5); margin-top: 3px; }
      .rpt-empty { padding: 40px; text-align: center; font-family: 'EB Garamond', serif; font-size: 16px; font-style: italic; color: rgba(74,26,8,0.35); }

      /* Table */
      .rpt-table-wrap { overflow-x: auto; }
      .rpt-table { width: 100%; border-collapse: collapse; font-family: 'EB Garamond', serif; font-size: 14px; }
      .rpt-table thead tr { background: linear-gradient(135deg, #2e0c0c, #4a1010); }
      .rpt-table thead th { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-bright); padding: 12px 16px; text-align: left; border-bottom: 2px solid var(--gold-mid); white-space: nowrap; }
      .rpt-tr { border-bottom: 1px solid rgba(201,151,58,0.1); transition: background 0.15s; }
      .rpt-tr:nth-child(even) { background: rgba(253,248,239,0.65); }
      .rpt-tr:hover { background: rgba(201,151,58,0.06); }
      .rpt-table td { padding: 11px 16px; color: var(--text-dark); vertical-align: middle; }
      .rpt-td-num { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; color: var(--gold-mid); text-align: center; width: 40px; }
      .rpt-td-receipt { font-family: 'Cinzel', serif; font-size: 11.5px; color: var(--gold-dim); letter-spacing: 0.5px; }
      .rpt-td-name { font-weight: 500; color: #2c1008; font-size: 14.5px; }
      .rpt-td-amount { font-family: 'Cinzel', serif; font-weight: 700; font-size: 13px; text-align: right; color: var(--green); white-space: nowrap; }
      .rpt-td-amount.expense { color: var(--red); }
      .rpt-tfoot-label { background: linear-gradient(135deg, #3d0c0c, #2e0c0c); font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-bright); padding: 11px 16px; text-align: right; }
      .rpt-tfoot-val { background: linear-gradient(135deg, #3d0c0c, #2e0c0c); font-family: 'Cinzel', serif; font-weight: 700; font-size: 14px; text-align: right; padding: 11px 16px; color: #f5e090; }
      .rpt-tfoot-val.expense { color: #f5b8b8; }
      .rpt-desk-only { display: table; width: 100%; }
      .rpt-mob-only { display: none; }

      /* Pills */
      .rpt-pill { display: inline-block; font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; padding: 3px 10px; border: 1px solid rgba(201,151,58,0.3); border-radius: 20px; color: var(--gold-dim); background: rgba(201,151,58,0.07); white-space: nowrap; }
      .rpt-pill-red { border-color: rgba(139,26,26,0.25); color: #8b1a1a; background: rgba(139,26,26,0.06); }
      .rpt-src-pill { display: inline-flex; align-items: center; gap: 4px; font-family: 'Cinzel', serif; font-size: 7px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 3px 9px; border-radius: 20px; border: 1px solid; white-space: nowrap; }
      .rpt-src-pill.online { color: #2a6b2a; background: rgba(42,107,42,0.08); border-color: rgba(42,107,42,0.25); }
      .rpt-src-pill.counter { color: #8a6020; background: rgba(138,96,32,0.08); border-color: rgba(138,96,32,0.25); }

      /* Mobile cards */
      .rpt-card-list { display: flex; flex-direction: column; gap: 0; }
      .rpt-mob-card { padding: 16px 18px; border-bottom: 1px solid rgba(201,151,58,0.1); background: #fff; transition: background 0.15s; }
      .rpt-mob-card:last-child { border-bottom: none; }
      .rpt-mob-card:active { background: rgba(201,151,58,0.04); }
      .rpt-mob-card-expense { border-left: 3px solid rgba(139,26,26,0.35); }
      .rpt-mob-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
      .rpt-mob-num { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; color: var(--gold-mid); background: rgba(201,151,58,0.08); border: 1px solid rgba(201,151,58,0.2); padding: 2px 8px; border-radius: 10px; }
      .rpt-mob-name { font-family: 'EB Garamond', serif; font-size: 16px; font-weight: 600; color: var(--text-dark); margin-bottom: 8px; line-height: 1.3; }
      .rpt-mob-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 5px; font-family: 'EB Garamond', serif; font-size: 13.5px; color: rgba(74,26,8,0.55); flex-wrap: wrap; }
      .rpt-mob-ic { font-size: 11px; margin-right: 5px; color: var(--gold-dim); }
      .rpt-mob-amount { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--green); }
      .rpt-mob-amount.expense { color: var(--red); }

      /* Summary section */
      .rpt-summary-section { background: linear-gradient(160deg, #2e0c0c 0%, #1a0404 80%); border-radius: 4px; overflow: hidden; border: 1px solid rgba(201,151,58,0.2); box-shadow: 0 4px 24px rgba(0,0,0,0.2); }
      .rpt-summary-header { padding: 20px 26px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .rpt-sumgrid { padding: 6px 0; }
      .rpt-sumrow { display: flex; align-items: center; justify-content: space-between; padding: 15px 30px; border-bottom: 1px solid rgba(201,151,58,0.08); transition: background 0.2s; }
      .rpt-sumrow:hover { background: rgba(201,151,58,0.05); }
      .rpt-sumrow:last-child { border-bottom: none; }
      .rpt-sumrow-label { display: flex; align-items: center; gap: 10px; font-family: 'EB Garamond', serif; font-size: 16px; color: rgba(232,196,106,0.68); }
      .rpt-sumrow-label svg { font-size: 14px; color: rgba(232,196,106,0.42); }
      .rpt-sumrow-val { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 700; }
      .rpt-sumrow-gross { background: rgba(42,107,42,0.08); border-top: 1px solid rgba(42,107,42,0.15); }
      .rpt-sumrow-balance.pos { background: linear-gradient(90deg, rgba(42,107,42,0.13), rgba(42,107,42,0.05)); border-top: 2px solid rgba(42,107,42,0.35); }
      .rpt-sumrow-balance.neg { background: linear-gradient(90deg, rgba(139,26,26,0.13), rgba(139,26,26,0.05)); border-top: 2px solid rgba(139,26,26,0.35); }
      .rpt-summary-foot { padding: 22px 30px 26px; display: flex; justify-content: center; }
      .rpt-pdf-big-btn { position: relative; overflow: hidden; display: inline-flex; align-items: center; gap: 10px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; padding: 14px 36px; background: linear-gradient(135deg, #6b4209 0%, var(--gold-mid) 25%, var(--gold-bright) 45%, #fdf0c8 55%, var(--gold-bright) 65%, var(--gold-mid) 80%, #6b4209 100%); color: var(--crimson-deep); border: none; cursor: pointer; clip-path: polygon(14px 0%,calc(100% - 14px) 0%,100% 50%,calc(100% - 14px) 100%,14px 100%,0% 50%); box-shadow: 0 5px 24px rgba(201,151,58,0.3); transition: transform 0.22s, box-shadow 0.22s, filter 0.22s; }
      .rpt-pdf-big-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(201,151,58,0.45); filter: brightness(1.05); }
      .rpt-pdf-btn-shimmer { position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.18); transform: skewX(-20deg); transition: left 0.5s ease; pointer-events: none; }
      .rpt-pdf-big-btn:hover .rpt-pdf-btn-shimmer { left: 130%; }

      /* ════ PDF MODAL ════ */
      .rpt-overlay { position: fixed; inset: 0; background: rgba(10,2,2,0.85); z-index: 9999; display: flex; align-items: flex-start; justify-content: center; padding: 20px; overflow-y: auto; backdrop-filter: blur(6px); }
      .rpt-pdf-modal { background: linear-gradient(160deg, #2e0c0c, #1c0505); border: 1px solid rgba(201,151,58,0.2); border-radius: 2px; width: 100%; max-width: 960px; overflow: hidden; box-shadow: 0 24px 80px rgba(0,0,0,0.7); }
      .rpt-pdf-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid rgba(201,151,58,0.15); gap: 12px; flex-wrap: wrap; }
      .rpt-pdf-modal-title { display: flex; align-items: center; gap: 12px; }
      .rpt-pdf-hicon { width: 36px; height: 36px; border-radius: 50%; background: rgba(139,26,26,0.15); border: 1px solid rgba(139,26,26,0.3); display: flex; align-items: center; justify-content: center; color: #c04040; font-size: 14px; flex-shrink: 0; }
      .rpt-pdf-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.35); }
      .rpt-pdf-modal-name { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--gold-bright); }
      .rpt-pdf-modal-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .rpt-pdf-action-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 8px 16px; border: 1px solid; cursor: pointer; background: none; transition: all 0.2s; clip-path: polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%); }
      .rpt-pdf-print { border-color: rgba(42,140,42,0.45); color: #7acc7a; }
      .rpt-pdf-print:hover { background: rgba(42,107,42,0.16); color: #a0eea0; }
      .rpt-pdf-download { border-color: rgba(201,151,58,0.45); color: var(--gold-bright); }
      .rpt-pdf-download:hover { background: rgba(201,151,58,0.13); color: #f5e090; }
      .rpt-pdf-download:disabled { opacity: 0.5; cursor: not-allowed; }
      .rpt-pdf-close-x { width: 34px; height: 34px; clip-path: none !important; border-radius: 50% !important; border-color: rgba(200,100,100,0.3) !important; color: rgba(220,130,130,0.7); padding: 0 !important; justify-content: center; }
      .rpt-pdf-close-x:hover { background: rgba(139,26,26,0.25); color: #fde8c8; }
      .rpt-pdf-spin { display: inline-block; animation: rptPdfSpin 1s linear infinite; }
      @keyframes rptPdfSpin { to{transform:rotate(360deg)} }
      .rpt-pdf-scroll { overflow-y: auto; max-height: calc(95vh - 70px); background: #3a3228; padding: 28px 24px; }
      .rpt-pdf-canvas { max-width: 860px; margin: 0 auto; }
      .rpt-pdf-paper { background: #fff; box-shadow: 0 8px 40px rgba(0,0,0,0.55); border-radius: 1px; overflow: hidden; font-family: 'EB Garamond', serif; }
      /* Paper interior styles mirroring print CSS */
      .rpt-pdf-paper .pdf-header { background: linear-gradient(135deg,#2e0c0c 0%,#5a1515 60%,#3d0c0c 100%); padding: 32px 44px 26px; position: relative; overflow: hidden; }
      .rpt-pdf-paper .pdf-header::before { content:'ॐ'; position:absolute; right:32px; top:50%; transform:translateY(-50%); font-size:110px; color:rgba(232,196,106,0.06); font-family:'Cinzel Decorative',cursive; line-height:1; pointer-events:none; }
      .rpt-pdf-paper .pdf-header-gold-bar { position:absolute; bottom:0; left:0; right:0; height:4px; background:linear-gradient(90deg,transparent 0%,#8a6020 20%,#e8c46a 50%,#8a6020 80%,transparent 100%); }
      .rpt-pdf-paper .pdf-hdr-row1 { display:flex; align-items:center; gap:18px; margin-bottom:16px; }
      .rpt-pdf-paper .pdf-hdr-seal { width:68px; height:68px; border-radius:50%; background:rgba(232,196,106,0.1); border:2px solid rgba(232,196,106,0.4); display:flex; align-items:center; justify-content:center; font-size:26px; color:#e8c46a; font-family:'Cinzel Decorative',cursive; flex-shrink:0; }
      .rpt-pdf-paper .pdf-hdr-temple-name { font-family:'Cinzel',serif; font-size:14px; font-weight:700; color:#e8c46a; letter-spacing:1.5px; }
      .rpt-pdf-paper .pdf-hdr-temple-sub { font-family:'EB Garamond',serif; font-size:12.5px; color:rgba(232,196,106,0.5); letter-spacing:1px; margin-top:5px; }
      .rpt-pdf-paper .pdf-hdr-divider { height:1px; background:linear-gradient(90deg,transparent,rgba(232,196,106,0.3),transparent); margin:0 0 14px; }
      .rpt-pdf-paper .pdf-hdr-row2 { display:flex; align-items:flex-end; justify-content:space-between; }
      .rpt-pdf-paper .pdf-hdr-report-label { font-family:'Cinzel',serif; font-size:10px; font-weight:600; letter-spacing:3px; text-transform:uppercase; color:rgba(232,196,106,0.55); margin-bottom:4px; }
      .rpt-pdf-paper .pdf-hdr-report-title { font-family:'Cinzel',serif; font-size:19px; font-weight:700; color:#e8c46a; letter-spacing:1.5px; }
      .rpt-pdf-paper .pdf-hdr-period { font-family:'EB Garamond',serif; font-size:14px; color:rgba(232,196,106,0.75); margin-top:5px; font-style:italic; }
      .rpt-pdf-paper .pdf-hdr-meta { text-align:right; }
      .rpt-pdf-paper .pdf-hdr-meta-line { font-family:'Cinzel',serif; font-size:10px; color:rgba(232,196,106,0.4); letter-spacing:1px; line-height:1.9; }
      .rpt-pdf-paper .pdf-body { padding:28px 44px 44px; }
      .rpt-pdf-paper .pdf-summary-strip { display:grid; grid-template-columns:repeat(5,1fr); border:1.5px solid #e0c070; border-radius:5px; overflow:hidden; margin-bottom:30px; }
      .rpt-pdf-paper .pdf-sc { padding:14px 10px; text-align:center; border-right:1px solid #e8d89a; }
      .rpt-pdf-paper .pdf-sc:last-child { border-right:none; }
      .rpt-pdf-paper .pdf-sc.income { background:linear-gradient(180deg,#f2fff2,#fff); }
      .rpt-pdf-paper .pdf-sc.expense { background:linear-gradient(180deg,#fff5f5,#fff); }
      .rpt-pdf-paper .pdf-sc.balance-pos { background:linear-gradient(180deg,#e8f5e8,#f0fff0); }
      .rpt-pdf-paper .pdf-sc.balance-neg { background:linear-gradient(180deg,#fce8e8,#fff5f5); }
      .rpt-pdf-paper .pdf-sc-label { font-family:'Cinzel',serif; font-size:7px; letter-spacing:1.5px; text-transform:uppercase; color:#8a6020; margin-bottom:7px; }
      .rpt-pdf-paper .pdf-sc-val { font-family:'Cinzel',serif; font-size:14px; font-weight:700; }
      .rpt-pdf-paper .pdf-sc-val.green { color:#1a5c1a; } .rpt-pdf-paper .pdf-sc-val.red { color:#8b1a1a; }
      .rpt-pdf-paper .pdf-sc-note { font-family:'EB Garamond',serif; font-size:11px; color:#b8a070; margin-top:4px; }
      .rpt-pdf-paper .pdf-section { margin-bottom:30px; }
      .rpt-pdf-paper .pdf-section-header { display:flex; align-items:stretch; gap:0; margin-bottom:12px; }
      .rpt-pdf-paper .pdf-sec-accent { width:5px; background:linear-gradient(180deg,#8b1a1a,#c9973a); border-radius:3px; flex-shrink:0; margin-right:12px; }
      .rpt-pdf-paper .pdf-sec-accent.green { background:linear-gradient(180deg,#2a6b2a,#5a9a5a); }
      .rpt-pdf-paper .pdf-sec-accent.gold { background:linear-gradient(180deg,#8a6020,#c9973a); }
      .rpt-pdf-paper .pdf-sec-title-block { flex:1; border-bottom:1.5px solid #e0c070; padding-bottom:7px; }
      .rpt-pdf-paper .pdf-sec-title { font-family:'Cinzel',serif; font-size:11.5px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:#3d0c0c; }
      .rpt-pdf-paper .pdf-sec-subtitle { font-family:'EB Garamond',serif; font-size:12px; color:#b8a070; margin-top:3px; }
      .rpt-pdf-paper table { width:100%; border-collapse:collapse; font-size:12.5px; font-family:'EB Garamond',serif; }
      .rpt-pdf-paper thead tr { background:linear-gradient(135deg,#2e0c0c,#4a1010); }
      .rpt-pdf-paper thead th { font-family:'Cinzel',serif; font-size:7.5px; letter-spacing:1.5px; text-transform:uppercase; color:#e8c46a; padding:10px 11px; text-align:left; font-weight:600; border-bottom:2px solid #c9973a; }
      .rpt-pdf-paper tbody tr { border-bottom:1px solid #f0e8d0; }
      .rpt-pdf-paper tbody tr:nth-child(even) { background:#fffbf3; }
      .rpt-pdf-paper tbody td { padding:9px 11px; color:#2c1008; vertical-align:top; line-height:1.45; }
      .rpt-pdf-paper tfoot tr { border-top:2px solid #c9973a; }
      .rpt-pdf-paper tfoot td { background:linear-gradient(135deg,#3d0c0c,#2e0c0c); font-family:'Cinzel',serif; font-size:11px; font-weight:700; padding:10px 11px; color:#e8c46a; letter-spacing:1px; }
      .rpt-pdf-paper .tfoot-amt { color:#f5e090; text-align:right; }
      .rpt-pdf-paper .td-num { text-align:center; color:#c9973a; font-family:'Cinzel',serif; font-size:10px; width:26px; font-weight:600; }
      .rpt-pdf-paper .td-amount { font-family:'Cinzel',serif; font-weight:700; text-align:right; color:#1a5c1a; white-space:nowrap; }
      .rpt-pdf-paper .td-amount.red { color:#8b1a1a; }
      .rpt-pdf-paper .td-pill { display:inline-block; font-family:'Cinzel',serif; font-size:7.5px; font-weight:700; letter-spacing:0.8px; text-transform:uppercase; padding:3px 8px; border:1px solid rgba(201,151,58,0.35); border-radius:20px; color:#7a5020; background:rgba(201,151,58,0.1); }
      .rpt-pdf-paper .pdf-fin-summary { border:1.5px solid #e0c070; border-radius:6px; overflow:hidden; margin-top:4px; }
      .rpt-pdf-paper .pdf-fs-row { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; border-bottom:1px solid #f0e8d0; }
      .rpt-pdf-paper .pdf-fs-row:last-child { border-bottom:none; }
      .rpt-pdf-paper .pdf-fs-label { font-family:'EB Garamond',serif; font-size:14px; color:#4a1a08; display:flex; align-items:center; gap:8px; }
      .rpt-pdf-paper .pdf-fs-label .dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
      .rpt-pdf-paper .pdf-fs-val { font-family:'Cinzel',serif; font-size:14px; font-weight:700; }
      .rpt-pdf-paper .pdf-fs-val.green { color:#1a5c1a; } .rpt-pdf-paper .pdf-fs-val.red { color:#8b1a1a; }
      .rpt-pdf-paper .pdf-fs-row.gross { background:rgba(42,107,42,0.06); }
      .rpt-pdf-paper .pdf-fs-row.gross .pdf-fs-label { font-family:'Cinzel',serif; font-size:12px; font-weight:700; color:#1a4a1a; letter-spacing:1px; }
      .rpt-pdf-paper .pdf-fs-row.gross .pdf-fs-val { font-size:16px; }
      .rpt-pdf-paper .pdf-fs-row.net-pos { background:linear-gradient(90deg,rgba(42,107,42,0.1),rgba(42,107,42,0.04)); border-top:2px solid #2a6b2a; }
      .rpt-pdf-paper .pdf-fs-row.net-neg { background:linear-gradient(90deg,rgba(139,26,26,0.1),rgba(139,26,26,0.04)); border-top:2px solid #8b1a1a; }
      .rpt-pdf-paper .pdf-fs-row.net-pos .pdf-fs-label, .rpt-pdf-paper .pdf-fs-row.net-neg .pdf-fs-label { font-family:'Cinzel',serif; font-size:13px; font-weight:700; letter-spacing:1px; }
      .rpt-pdf-paper .pdf-fs-row.net-pos .pdf-fs-val, .rpt-pdf-paper .pdf-fs-row.net-neg .pdf-fs-val { font-size:20px; }
      .rpt-pdf-paper .pdf-footer { margin-top:40px; padding:18px 0 0; border-top:1px solid #e0c070; display:flex; align-items:center; justify-content:space-between; gap:20px; }
      .rpt-pdf-paper .pdf-footer-text { font-family:'EB Garamond',serif; font-size:11px; color:#c0a878; line-height:1.8; font-style:italic; }
      .rpt-pdf-paper .pdf-footer-om { font-size:28px; color:rgba(139,26,26,0.18); }
      .rpt-pdf-paper .pdf-footer-seal-text { font-family:'Cinzel',serif; font-size:7px; letter-spacing:2px; color:#d4b87a; text-transform:uppercase; text-align:center; margin-top:3px; }

      /* Toast */
      .mb-toast-wrap { position: fixed; top: 20px; right: 20px; z-index: 99999; }
      .mb-toast { position: relative; display: flex; align-items: center; gap: 14px; background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid var(--tb); clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%); padding: 14px 42px 14px 14px; min-width: 270px; max-width: 360px; box-shadow: 0 12px 40px rgba(26,4,4,0.2); }
      .mb-toast-bar { position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--ta), transparent); }
      .mb-mc { position: absolute; width: 6px; height: 6px; border-color: var(--tb); border-style: solid; }
      .mb-mc.tl { top: 3px; left: 3px; border-width: 1px 0 0 1px; }
      .mb-mc.tr { top: 3px; right: 3px; border-width: 1px 1px 0 0; }
      .mb-mc.bl { bottom: 3px; left: 3px; border-width: 0 0 1px 1px; }
      .mb-mc.br { bottom: 3px; right: 3px; border-width: 0 1px 1px 0; }
      .mb-toast-icon { width: 34px; height: 34px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
      .mb-toast-body { flex: 1; }
      .mb-toast-title { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2px; }
      .mb-toast-msg { font-family: 'EB Garamond', serif; font-size: 13.5px; color: var(--text-mid); line-height: 1.4; }
      .mb-toast-x { position: absolute; top: 8px; right: 10px; background: none; border: none; cursor: pointer; font-size: 11px; }

      /* ════ RESPONSIVE ════ */
      @media (max-width: 900px) {
        .rpt-hero { grid-template-columns: 48px 1fr 48px; }
        .rpt-hero-title { font-size: 22px; }
        .rpt-stat-val { font-size: 11px !important; }
        .rpt-body { padding: 20px 20px 50px; }
        .rpt-toolbar { padding: 10px 20px; }
        .rpt-result-info { padding: 7px 20px; }
        .rpt-filter-grid { padding: 16px 20px; }
      }
      @media (max-width: 640px) {
        .rpt-desk-only { display: none !important; }
        .rpt-mob-only { display: flex !important; flex-direction: column; }
        .rpt-hero { grid-template-columns: 32px 1fr 32px; }
        .rpt-hero-side { padding: 18px 0; gap: 5px; }
        .rpt-side-om { font-size: 13px; }
        .rpt-side-sym { font-size: 11px; }
        .rpt-hero-center { padding: 22px 10px 18px; }
        .rpt-hero-title { font-size: 18px; letter-spacing: 0.5px; }
        .rpt-hero-sub { font-size: 12px; display: none; }
        .rpt-tab-selector { gap: 5px; }
        .rpt-tab-btn { padding: 10px 8px; font-size: 8px; letter-spacing: 0.8px; gap: 5px; }
        .rpt-tab-icon { font-size: 11px; }
        .rpt-tab-label { display: none; }
        .rpt-tab-count { display: none; }
        .rpt-stat-row { max-width: 100%; }
        .rpt-stat-val { font-size: 10px !important; }
        .rpt-stat-lbl { font-size: 6px; }
        .rpt-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 8px; }
        .rpt-toolbar-end { margin-left: 0; justify-content: stretch; }
        .rpt-toolbar-end .rpt-action-btn { flex: 1; justify-content: center; }
        .rpt-filter-btn { width: 100%; justify-content: space-between; }
        .rpt-result-info { padding: 7px 14px; font-size: 13px; }
        .rpt-body { padding: 12px 12px 50px; }
        .rpt-filter-grid { grid-template-columns: 1fr; padding: 14px 14px; }
        .rpt-fi-range { grid-column: span 1; }
        .rpt-range-row { flex-direction: column; }
        .rpt-range-sep { display: none; }
        .rpt-mode-tabs { gap: 4px; }
        .rpt-mode-tab { clip-path: none; border-radius: 20px; font-size: 7.5px; padding: 6px 10px; }
        .rpt-section-header { padding: 14px 16px; }
        .rpt-sumrow { padding: 13px 18px; flex-direction: column; align-items: flex-start; gap: 4px; }
        .rpt-sumrow-val { font-size: 17px !important; }
        .rpt-summary-foot { padding: 16px 18px; }
        .rpt-pdf-big-btn { clip-path: none; border-radius: 28px; width: 100%; justify-content: center; font-size: 8px; padding: 13px 20px; }
        .rpt-pdf-modal-header { padding: 10px 12px; flex-direction: column; align-items: flex-start; gap: 8px; }
        .rpt-pdf-modal-actions { width: 100%; justify-content: stretch; }
        .rpt-pdf-action-btn { flex: 1; justify-content: center; clip-path: none; border-radius: 20px; font-size: 8px; padding: 8px 10px; }
        .rpt-pdf-close-x { flex: 0 0 36px !important; }
        .rpt-pdf-scroll { padding: 16px 10px; }
        .rpt-pdf-paper .pdf-body { padding: 20px 16px 32px; }
        .rpt-pdf-paper .pdf-header { padding: 22px 16px 18px; }
        .rpt-pdf-paper .pdf-summary-strip { grid-template-columns: repeat(3,1fr); }
        .rpt-mob-card { padding: 14px 15px; }
        .rpt-mob-name { font-size: 15px; }
        .rpt-mob-amount { font-size: 13px; }
      }
      @media (max-width: 400px) {
        .rpt-hero { grid-template-columns: 24px 1fr 24px; }
        .rpt-hero-side { padding: 14px 0; }
        .rpt-side-om { font-size: 10px; }
        .rpt-hero-title { font-size: 15px; }
        .rpt-tab-btn { padding: 9px 6px; }
        .rpt-period-chip { font-size: 7.5px; padding: 5px 10px; }
      }
    `}</style>
  );
}